import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { DxDataGridModule, DxFormModule } from 'devextreme-angular';
import { HomeComponent } from './ag-crm/home/home.component';
import { ProfileComponent } from './ag-crm/profile/profile.component';
import { LoginFormComponent } from './layouts/components/login-form/login-form.component';
import { AppRoutes } from './app.model';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { AuthGuard } from './shared/services/util/auth.guard';
import { AgCampaignsGuard } from './ag-campaigns/ag-campaigns.guard';
import { AgAdministrationGuard } from './admin-crm/admin-crm.guard';
import { CarriersGuard } from './ag-crm/carriers/carriers.guard';
import { RegistrantsGuard } from './ag-crm/registrants/registrants.guard';
import { ProspectsGuard } from './ag-crm/prospects/prospects.guard';
import { AgenciesGuard } from './ag-crm/agencies/agencies.guard';
import { AgentsGuard } from './ag-crm/agents/agents.guard';
import { ConferencesGuard } from './ag-crm/registrations/conferences/conferences.guard';
import { AccessDeniedComponent } from './layouts/components/access-denied/access-denied.component';
import { AgNotificationsHubGuard } from './ag-notifications-hub/ag-notifications-hub.guard';
import { ConferenceRegistrationGuard } from './ag-crm/registrations/conference-registration-grid/conference-registration.guard';
import { ConferenceRegistrationsGridComponent } from './ag-crm/registrations/conference-registration-grid/conference-registration-grid.component';
import { FormsListGuard } from './ag-crm/reports/forms-list/forms-list.guard';
import { AgCarriersProductionReportingGuard } from './ag-carriers-production-reporting/ag-carriers-production-reporting.guard';
import { AgentRegistryGuard } from './ag-carriers-production-reporting/agent-registry/agent-registry.guard';
import { CarriersProductionReportingRoutes } from './ag-carriers-production-reporting/ag-carriers-production-reporting.model';
import { ReportsViewerGuard } from './reports-viewer-page/reports-viewer.guard';
import { DashboardGuard } from './dashboard/dashboard.guard';
import { TempReactHostComponent } from './temp-react-host/temp-react-host.component';
import { MenuItemsConstants } from 'ag-common-lib/public-api';
import { formatPageTitle } from 'ag-common-svc/public-api';

const crmMenu = MenuItemsConstants.crm;

const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: AppRoutes.LoginForm },
  {
    path: AppRoutes.LoginForm,
    component: LoginFormComponent,
    title: crmMenu.auth.login,
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: AppRoutes.Dashboard,
        loadChildren: () => import('./dashboard/dashboard.module').then(mod => mod.DashboardModule),
        canActivate: [DashboardGuard],
      },
      {
        path: AppRoutes.Agents,
        loadChildren: () => import('./ag-crm/agents/agents.module').then(mod => mod.AgentsModule),
        canActivate: [AgentsGuard],
      },
      {
        path: AppRoutes.Agencies,
        loadChildren: () => import('./ag-crm/agencies/agencies.module').then(mod => mod.AgenciesModule),
        canActivate: [AgenciesGuard],
      },
      {
        path: AppRoutes.Prospects,
        loadChildren: () => import('./ag-crm/prospects/prospects.module').then(mod => mod.ProspectsModule),
        canActivate: [ProspectsGuard],
      },
      {
        path: AppRoutes.Registrants,
        loadChildren: () => import('./ag-crm/registrants/registrants.module').then(mod => mod.RegistrantsModule),
        canActivate: [RegistrantsGuard],
      },
      {
        path: AppRoutes.Carriers,
        loadChildren: () => import('./ag-crm/carriers/carriers.module').then(mod => mod.CarriersModule),
        canActivate: [CarriersGuard],
      },
      {
        path: AppRoutes.NotificationsHub,
        loadChildren: () =>
          import('./ag-notifications-hub/ag-notifications-hub.module').then(mod => mod.AgNotificationsHubModule),
        canActivate: [AgNotificationsHubGuard],
      },
      {
        path: AppRoutes.Campaign,
        loadChildren: () => import('./ag-campaigns/ag-campaigns.module').then(mod => mod.CampaignsModule),
        canActivate: [AgCampaignsGuard],
      },
      {
        path: AppRoutes.AgentRegistry,
        title: formatPageTitle([crmMenu.reports.reports, crmMenu.reports.agentIds]),
        loadComponent: () =>
          import('./ag-carriers-production-reporting/agent-registry/agent-registry-grid.component').then(
            mod => mod.AgentRegistryGridComponent,
          ),
        canActivate: [AgentRegistryGuard],
      },
      {
        path: AppRoutes.DownlineImports,
        title: formatPageTitle([crmMenu.reports.reports, crmMenu.reports.downlineFiles]),
        loadComponent: () =>
          import('./ag-carriers-production-reporting/downline-imports/downline-imports.component').then(
            mod => mod.DownlineImportsComponent,
          ),
        canActivate: [AgentRegistryGuard],
      },
      /**
       * Both screens used to live under `carriers-production-reporting`. These two
       * redirects keep saved links working, and they are declared ahead of that
       * section so its `reportsGenerationPipeline` guard never gets to refuse a link
       * to a page it no longer owns.
       */
      {
        path: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.AgentRegistry}`,
        redirectTo: `/${AppRoutes.AgentRegistry}`,
        pathMatch: 'full',
      },
      {
        path: `${AppRoutes.CarriersProductionReporting}/${CarriersProductionReportingRoutes.DownlineImports}`,
        redirectTo: `/${AppRoutes.DownlineImports}`,
        pathMatch: 'full',
      },
      {
        path: AppRoutes.CarriersProductionReporting,
        loadChildren: () =>
          import('./ag-carriers-production-reporting/ag-carriers-production-reporting.module').then(
            mod => mod.CarriersProductionReportingModule,
          ),
        canActivate: [AgCarriersProductionReportingGuard],
      },
      {
        path: AppRoutes.ReportsViewer,
        title: formatPageTitle([
          crmMenu.reports.reports,
          crmMenu.reports.carriersProductionReporting,
          crmMenu.reports.reportsViewer,
        ]),
        loadChildren: () =>
          import('./reports-viewer-page/reports-viewer-page.module').then(mod => mod.ReportsViewerPageModule),
        canActivate: [ReportsViewerGuard],
      },
      {
        path: AppRoutes.Administration,
        loadChildren: () => import('./admin-crm/admin-crm.module').then(mod => mod.AdminCrmModule),
        canActivate: [AgAdministrationGuard],
      },
      {
        path: AppRoutes.Events,
        loadChildren: () =>
          import('./ag-crm/registrations/conferences/conferences.module').then(mod => mod.ConferencesModule),
        canActivate: [ConferencesGuard],
      },
      {
        path: AppRoutes.ConferenceRegistrations,
        title: formatPageTitle([crmMenu.registrations.registrations, crmMenu.registrations.eventRegistrations]),
        component: ConferenceRegistrationsGridComponent,
        canActivate: [ConferenceRegistrationGuard],
      },

      { path: AppRoutes.Home, component: HomeComponent, title: crmMenu.app.home },
      { path: AppRoutes.Profile, component: ProfileComponent, title: crmMenu.userMenu.profile },
      {
        path: AppRoutes.Forms,
        loadChildren: () => import('./ag-crm/reports/forms-list/forms-list.module').then(mod => mod.FormsListModule),
        canActivate: [FormsListGuard],
      },

      { path: AppRoutes.AccessDenied, component: AccessDeniedComponent, title: crmMenu.app.accessDenied },
    ],
  },
  {
    path: AppRoutes.TempReact,
    canActivate: [AuthGuard],
    component: TempReactHostComponent,
  },
  { path: '**', redirectTo: AppRoutes.Home },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true }), DxDataGridModule, DxFormModule],
  exports: [RouterModule],
})
export class AppRoutingModule {}
